import { Box, Button, Typography } from '@mui/material';
import { toast } from '@side/pantry';
import { useEffect, useRef } from 'react';
import { navigateTo } from '../../utils/navigate';

// Lightweight admin-ui route whose only job is to prove IAP + Okta both
// authenticated. Serving it with a 200 is what this iframe's auth probe
// detects; its broadcast/postMessage are secondary. Kept in sync with
// admin-ui src/app/iap-bootstrap/page.tsx.
const IAP_BOOTSTRAP_PATH = '/admin/iap-bootstrap';
const SUCCESS_MESSAGE = 'side-iap-auth:success';
const SUCCESS_CHANNEL = 'side-iap-auth';
/** How often to ask the server whether IAP + session auth completed. */
const AUTH_PROBE_INTERVAL_MS = 1500;
/** Stop polling eventually if the user abandons the popup. */
const AUTH_PROBE_MAX_ATTEMPTS = 120;

/**
 * Ask the server whether this iframe's credentials now clear both gates.
 *
 * A same-origin fetch of the bootstrap page: 200 means the IAP cookie and
 * the identity session are both present and sent from THIS (third-party
 * iframe) context — the exact condition the destination needs. While IAP
 * isn't satisfied it answers with a redirect to Google's login, surfaced as
 * an opaqueredirect (status 0) under redirect: 'manual'.
 *
 * This server probe is the only reliable completion signal we have left:
 * COOP (same-origin) severs window.opener across the popup's cross-origin
 * auth hops, and Chrome's third-party storage partitioning puts this
 * iframe's BroadcastChannel in the Zendesk partition while the popup's is
 * in the top-level one, so neither client-side channel crosses.
 */
async function isAuthReady(): Promise<boolean> {
  try {
    const response = await fetch(IAP_BOOTSTRAP_PATH, {
      credentials: 'include',
      cache: 'no-store',
      redirect: 'manual',
    });
    return response.ok;
  } catch {
    return false;
  }
}

type EmbeddedLoginProps = {
  /** Same-origin admin-ui path to load once auth succeeds, e.g. /admin/users/123 */
  destination: string;
};

/**
 * Auth prompt shown when identity-ui /login renders inside the Zendesk embed
 * iframe with an IAP-gated (/admin) destination.
 *
 * admin-ui sits behind Google IAP, and both IAP's Google login and Okta's login
 * refuse to be framed (X-Frame-Options), so we can't authenticate inside the
 * cross-site iframe. Instead we open a top-level popup that runs Okta then IAP
 * (/identity/okta-login -> /redirect -> /admin/iap-bootstrap), establishing both
 * cookies on this origin. Ordering Okta first means the session already exists
 * by the time the popup reaches /admin, so admin-ui's only redirect is IAP's own
 * (Google), which returns to the bootstrap page rather than bouncing away.
 *
 * When the bootstrap popup signals success we navigate the iframe to the real
 * destination, which now renders with both cookies in place.
 *
 * Note: the embedded iframe is cross-site to the Zendesk top-level page, so the
 * (Google-managed, non-partitionable) IAP cookie only reaches admin-ui while the
 * browser still sends SameSite=None third-party cookies. See PR for the
 * longer-term options.
 */
// [iap-embed-debug] Client-side trail for the popup auth handshake. The flow
// spans Zendesk → identity-ui iframe → popup → admin-ui and has failed in prod
// twice in ways only diagnosable from logs — keep until the embed is GA'd.
// biome-ignore-start lint/suspicious/noConsole: embed debug logging
function debugLog(event: string, details?: Record<string, unknown>) {
  console.log(`[iap-embed-debug] EmbeddedLogin ${event}`, details ?? {});
}
// biome-ignore-end lint/suspicious/noConsole: embed debug logging

export function EmbeddedLogin({ destination }: EmbeddedLoginProps) {
  const popupRef = useRef<Window | null>(null);
  const probeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigatedRef = useRef(false);
  const onSuccessRef = useRef<((source: string) => void) | null>(null);

  useEffect(() => {
    debugLog('mounted', {
      destination,
      framed: window.self !== window.top,
      origin: window.location.origin,
    });
    const onSuccess = (source: string) => {
      if (navigatedRef.current) {
        return;
      }
      navigatedRef.current = true;
      if (probeTimerRef.current) {
        clearInterval(probeTimerRef.current);
        probeTimerRef.current = null;
      }
      // The popup may already be unreachable/un-closable: our pages send
      // Cross-Origin-Opener-Policy: same-origin, so the popup's cross-origin
      // hops (Okta, IAP's Google login) switch its browsing context group and
      // disconnect the window proxy. close() is best-effort; the bootstrap
      // page shows its own "you can close this window" fallback.
      try {
        popupRef.current?.close();
      } catch {
        // disconnected proxy — ignore
      }
      // Navigate the iframe to admin-ui; the IAP + session cookies now exist.
      debugLog('auth success — navigating iframe', { source, destination });
      navigateTo(destination);
    };
    onSuccessRef.current = onSuccess;

    // PRIMARY signal: ask the server. If auth is ALREADY complete (page
    // refresh after a prior popup run, or another ticket's iframe finished
    // the flow), skip the button entirely and navigate now. The popup path
    // below also relies on this probe, via the interval handleLogin starts.
    isAuthReady().then((ready) => {
      debugLog('mount auth probe', { ready });
      if (ready) {
        onSuccess('mount-probe');
      }
    });

    // SECONDARY signal: BroadcastChannel. Same-origin delivery that survives
    // COOP (window.opener is severed by the popup's cross-origin auth hops),
    // but NOT Chrome's third-party storage partitioning — this iframe lives
    // in the Zendesk partition while the popup is top-level, so in Chrome
    // this never fires; it covers browsers without partitioning. Channel
    // name shared with admin-ui src/app/iap-bootstrap/page.tsx.
    const channel =
      typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(SUCCESS_CHANNEL)
        : null;
    if (channel) {
      channel.onmessage = (event: MessageEvent) => {
        debugLog('broadcast received', { type: event.data?.type ?? null });
        if (event.data?.type === SUCCESS_MESSAGE) {
          onSuccess('broadcast-channel');
        }
      };
    } else {
      debugLog('BroadcastChannel unavailable — relying on opener postMessage');
    }

    // FALLBACK signal: opener postMessage (works only if the opener
    // relationship somehow survived, e.g. COOP relaxed in the future).
    const onMessage = (event: MessageEvent) => {
      // The popup and bootstrap page are same-origin as this page.
      if (event.origin !== window.location.origin) {
        return;
      }
      debugLog('message received', {
        type: event.data?.type ?? null,
        matchesSuccess: event.data?.type === SUCCESS_MESSAGE,
      });
      if (event.data?.type === SUCCESS_MESSAGE) {
        onSuccess('opener-postmessage');
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      onSuccessRef.current = null;
      if (probeTimerRef.current) {
        clearInterval(probeTimerRef.current);
        probeTimerRef.current = null;
      }
      channel?.close();
      window.removeEventListener('message', onMessage);
      // Close an orphaned popup if we unmount before it signals success
      // (e.g. the user navigates away from /login).
      try {
        popupRef.current?.close();
      } catch {
        // disconnected proxy — ignore
      }
    };
  }, [destination]);

  const handleLogin = () => {
    const popupUrl = `${window.location.origin}/identity/okta-login?destination=${encodeURIComponent(
      IAP_BOOTSTRAP_PATH,
    )}`;
    // Must run from this click (a user gesture) or the browser blocks the popup.
    popupRef.current = window.open(
      popupUrl,
      'side-iap-auth',
      'popup,width=480,height=640',
    );
    debugLog('popup opened', { popupUrl, blocked: !popupRef.current });
    if (!popupRef.current) {
      toast('error', 'Pop-up blocked. Allow pop-ups for Zendesk, then retry.');
      return;
    }

    // Poll the server for auth completion while the popup runs — the only
    // signal Chrome's storage partitioning can't block (see isAuthReady).
    if (probeTimerRef.current) {
      clearInterval(probeTimerRef.current);
    }
    let attempts = 0;
    probeTimerRef.current = setInterval(() => {
      attempts += 1;
      if (attempts > AUTH_PROBE_MAX_ATTEMPTS) {
        debugLog('auth probe gave up', { attempts });
        if (probeTimerRef.current) {
          clearInterval(probeTimerRef.current);
          probeTimerRef.current = null;
        }
        return;
      }
      isAuthReady().then((ready) => {
        if (ready) {
          onSuccessRef.current?.('auth-probe');
        }
      });
    }, AUTH_PROBE_INTERVAL_MS);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        height: '100vh',
        p: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="body1">
        Sign in to Side to view this customer.
      </Typography>
      <Button variant="contained" onClick={handleLogin}>
        Sign in to Side
      </Button>
    </Box>
  );
}
